/**
 * Format an integer with thousands separator (user-locale) with fallback to en-US.
 */
const formatInt = (value: number) => {
	try {
		return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value);
	} catch {
		return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(value);
	}
};

// Magnitude bounds outside of which JS Number.toString() switches to scientific
// notation (per ECMAScript spec). Inside these bounds toString() emits fixed-form
// output that toFixed() can safely process; outside, toFixed() would
// misrepresent the value (e.g. (1e-7).toFixed(6) === "0.000000").
const SCIENTIFIC_NOTATION_LOWER_BOUND = 1e-6;
const SCIENTIFIC_NOTATION_UPPER_BOUND = 1e21;

/**
 * Find the maximum number of decimal places present across a list of numbers.
 *
 * Recovers a uniform precision so a list like [5, 5.5, 6] can be rendered as "5.0", "5.5", "6.0" —
 * JS number parsing drops trailing zeros at parse time, so the only way to know "5" should render
 * as "5.0" is to observe that other values in the same list carry one decimal.
 *
 * This commonly comes up with JSON serializers, which drop trailing zeros because the numeric type
 * carries no precision metadata. Even in pure JS:
 *
 * ```javascript
 * JSON.stringify([5.000, 6.120, 7.123, 9.999])
 * // '[5,6.12,7.123,9.999]'
 * ```
 * The producer's intended precision is gone before the consumer ever sees it, so per-value recovery
 * is impossible. Scanning the whole list is the standard workaround.
 *
 * Capped at `maxCap` (default 6) to defend against floating-point noise (e.g. 0.1 + 0.2). Ignores
 * non-finite values. Values whose magnitude falls outside the cap's resolution (i.e. would render
 * in scientific notation) are also ignored, since their decimal precision cannot be recovered
 * meaningfully under the cap.
 */
function maxDecimalPlaces(values: number[], maxCap: number = 6): number {
	let max = 0;
	for (const v of values) {
		if (!Number.isFinite(v)) continue;
		const absV = Math.abs(v);
		const usesScientificNotation =
			absV !== 0 && (absV < SCIENTIFIC_NOTATION_LOWER_BOUND || absV >= SCIENTIFIC_NOTATION_UPPER_BOUND);
		if (usesScientificNotation) continue;
		const str = v.toFixed(maxCap);
		const dot = str.indexOf(".");
		if (dot < 0) continue;
		const trimmed = str.slice(dot + 1).replace(/0+$/, "");
		if (trimmed.length > max) max = trimmed.length;
		if (max >= maxCap) return maxCap;
	}
	return max;
}

const BYTE_UNITS = ["B", "KB", "MB", "GB", "TB", "PB"];

/**
 * Format a byte count as a human-readable string using binary (1024-based) units, e.g.
 * `formatBytes(3007971091)` → `"2.80 GB"`. Uses the conventional KB/MB/GB labels for 1024-based
 * magnitudes (matching how OS file managers and Azure Storage Explorer display sizes).
 *
 * @param bytes - The number of bytes. Negative or non-finite input returns "0 B".
 * @param fractionDigits - Decimal places for non-byte units (default 2). Whole bytes never show
 *   decimals (e.g. "512 B", not "512.00 B").
 */
function formatBytes(bytes: number, fractionDigits: number = 2): string {
	if (!Number.isFinite(bytes) || bytes <= 0) {
		return "0 B";
	}
	const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), BYTE_UNITS.length - 1);
	const value = bytes / 1024 ** exponent;
	const digits = exponent === 0 ? 0 : fractionDigits;
	return `${value.toFixed(digits)} ${BYTE_UNITS[exponent]}`;
}

/**
 * Find decimal places based on following gating:
 * * `[0, 1.0)` = 4 decimal points
 * * `[1.0, 1000)` = 2 decimal points
 * * `[1000, ∞)` = 0 decimal points
 */
function getDecimalsByGate(value: number) {
	if (value < 1) return 4;
	if (value >= 1 && value < 1000) return 2;
	return 0;
}

export { formatInt, maxDecimalPlaces, formatBytes, getDecimalsByGate };
